/**
 * The operator's own recovery codes, inside the Passkeys dialog (kernel apps stage 5, WS-31b,
 * issue #3806, R-150, R-152).
 *
 * cp-auth-api issues ten recovery codes with the first passkey and shows them once in the verify
 * answer (WS-31a1). This panel is the signed-in half: it shows how many codes are unused, and it
 * makes ten new codes on request, which replaces every older code. The new codes show once, in
 * `OneTimeSecretDialog`, and the panel keeps them in React state only until the operator closes the
 * reveal. No route shows them again.
 *
 * The routes are self-service (`passkeys.api.ts`): the server takes the operator from the token, so
 * this panel asks for no permission. `POST` also needs a session that passed the passkey; a
 * refusal shows the server's sentence.
 *
 * A replace is a click and a confirm, with no typed name: it is recoverable (the operator holds the
 * new codes), and the typed gate of `destructive-confirm.tsx` is for the irreversible.
 */
import { useEffect, useRef, useState } from 'react';
import { RECOVERY_CODE_COUNT } from '@zion/sdk-contracts';
import { displayMessage } from '../platform/errors';
import { useApiResource } from '../panes/async-state';
import { focusPanel } from '../panes/dialog-panel';
import { RECOVERY_CODES_PATH, getRecoveryCodesStatus, replaceRecoveryCodes } from '../api/passkeys.api';
import { Button } from './Button';
import { ErrorState } from './absence';
import { OneTimeSecretDialog } from './OneTimeSecretDialog';

/**
 * The sentence for `remaining` unused codes. Zero names the risk and the remedy; one and more give
 * the count out of `RECOVERY_CODE_COUNT`. Pure.
 */
export function recoveryCodesCountText(remaining: number): string {
  if (remaining === 0) {
    return 'You have no unused recovery code. Make new codes, so that you can sign in if you lose your passkey.';
  }
  return `${remaining} of ${RECOVERY_CODE_COUNT} recovery code${remaining === 1 ? ' is' : 's are'} unused.`;
}

/** The confirm sentence of a replace. */
export const RECOVERY_CODES_REPLACE_TEXT = 'Make ten new codes? Every older code stops working.';

export interface RecoveryCodesPanelProps {
  /**
   * `true` from the confirm of a replace, before the POST leaves, until the operator closed the new
   * codes; `false` after that close, or after a replace that failed. The Passkeys dialog holds its
   * own close while `true`, so Done, Escape and the backdrop cannot unmount the panel while the
   * server replaces the codes, nor throw away codes the operator has not saved (WS-31b-R11). The
   * server never sends the codes again.
   */
  readonly onRevealChange: (revealing: boolean) => void;
}

/**
 * The count, "Make new codes", its confirm, and the one-time reveal.
 *
 * - Loading: a plain line. The dialog's passkey list owns the one `role="status"` of the read, so
 *   a screen reader hears one announcement. A failed read: `ErrorState` with a retry, never a count.
 * - "Make new codes" arms the confirm; "Cancel" disarms it. The confirm calls
 *   `onRevealChange(true)` and then posts once.
 * - A pass shows the ten codes, and reads the count again after the operator closes the reveal
 *   (`onRevealChange(false)`).
 * - A failure shows `displayMessage` of the error, calls `onRevealChange(false)` and keeps the
 *   confirm.
 *
 * Focus (WS-31b-R26). Each of these buttons unmounts in the commit that its click makes, and focus
 * on a removed node falls to `<body>`, outside the dialog's Tab trap and Escape handler. So the
 * panel moves focus itself, after the commit:
 * - "Make new codes": to the confirm group, whose sentence says what the confirm does. The
 *   destructive button never takes focus first (the rule of `panes/dialog-panel.tsx`).
 * - "Cancel", and the Close of the reveal: to "Make new codes".
 * - A failed replace: to the confirm group, beside the error.
 * - A pass: the reveal's `DialogPanel` takes focus on its own mount.
 */
export function RecoveryCodesPanel({ onRevealChange }: RecoveryCodesPanelProps): React.JSX.Element {
  const status = useApiResource(RECOVERY_CODES_PATH, getRecoveryCodesStatus);
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<readonly string[] | null>(null);
  const confirmRef = useRef<HTMLDivElement | null>(null);
  const replaceRef = useRef<HTMLButtonElement | null>(null);
  /** Where focus goes after the next commit, or `null`. Set by the handler that unmounts the focus. */
  const focusNext = useRef<'confirm' | 'replace' | null>(null);

  /*
   * After every commit, move focus where the last handler asked. No dependency list: the target
   * mounts in the commit that the handler's state change makes, and this runs after that commit.
   * It also runs after the passive cleanup of the reveal's `DialogPanel`, whose `restoreFocus`
   * would otherwise leave focus on the body it captured as the opener.
   */
  useEffect(() => {
    const target = focusNext.current;
    if (target === null) return;
    focusNext.current = null;
    focusPanel(target === 'confirm' ? confirmRef.current : replaceRef.current);
  });

  async function replace(): Promise<void> {
    setBusy(true);
    setError(null);
    // ⛔ Before the POST leaves (WS-31b-R11): the server replaces every code whatever the client
    // does, so a close while the request is out would unmount the only place the codes can show.
    onRevealChange(true);
    try {
      const issued = await replaceRecoveryCodes();
      setArmed(false);
      setCodes(issued.recoveryCodes);
    } catch (cause) {
      setError(displayMessage(cause, 'Could not make new recovery codes.'));
      onRevealChange(false);
      focusNext.current = 'confirm';
    } finally {
      setBusy(false);
    }
  }

  function closeReveal(): void {
    setCodes(null);
    onRevealChange(false);
    focusNext.current = 'replace';
    status.reload();
  }

  function count(): React.JSX.Element {
    if (status.state.kind === 'loading') {
      return (
        <p data-testid="recovery-codes-loading" className="text-xs text-muted">
          Reading your recovery codes…
        </p>
      );
    }
    if (status.state.kind === 'error') {
      return (
        <ErrorState
          subject="your recovery codes"
          message={status.state.message}
          status={status.state.status}
          onRetry={status.reload}
          testId="recovery-codes-errorstate"
        />
      );
    }
    return (
      <p data-testid="recovery-codes-count" className="max-w-[100ch] text-xs text-secondary">
        {recoveryCodesCountText(status.state.data.remaining)}
      </p>
    );
  }

  return (
    <section aria-labelledby="recovery-codes-heading" className="mt-6" data-testid="recovery-codes">
      <h3 id="recovery-codes-heading" className="text-sm font-semibold text-primary">
        Recovery codes
      </h3>
      <p className="mt-1 max-w-[100ch] text-xs text-muted">
        A recovery code replaces your passkey once at sign-in, when your passkey is lost.
      </p>
      <div className="mt-2 flex flex-col gap-2">
        {count()}
        {codes === null ? null : (
          <OneTimeSecretDialog
            testId="recovery-codes-reveal"
            copyLabel="Copy the recovery codes"
            titleNode={
              <h4 id="recovery-codes-reveal-title" className="text-sm font-semibold text-primary">
                Ten new recovery codes
              </h4>
            }
            bodyCopy={
              <p className="max-w-[100ch] text-xs text-secondary">
                Every older code stopped working. The control plane shows these codes only now.
              </p>
            }
            secret={codes}
            ackLabel="I saved these recovery codes."
            onClose={closeReveal}
          />
        )}
        {codes !== null ? null : armed ? (
          <div
            ref={confirmRef}
            role="group"
            aria-labelledby="recovery-codes-confirm-text"
            tabIndex={-1}
            data-testid="recovery-codes-confirm"
            className="flex flex-col gap-2"
          >
            <p id="recovery-codes-confirm-text" className="max-w-[100ch] text-xs text-secondary">
              {RECOVERY_CODES_REPLACE_TEXT}
            </p>
            <div className="flex items-center gap-2">
              <Button tone="danger" size="sm" disabled={busy} onClick={() => void replace()} data-testid="recovery-codes-confirm-replace">
                {busy ? 'Working…' : 'Make new codes'}
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  focusNext.current = 'replace';
                  setArmed(false);
                }}
                data-testid="recovery-codes-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button
              ref={replaceRef}
              size="sm"
              onClick={() => {
                focusNext.current = 'confirm';
                setArmed(true);
              }}
              data-testid="recovery-codes-replace"
            >
              Make new codes
            </Button>
          </div>
        )}
        {error === null ? null : (
          <p role="alert" data-testid="recovery-codes-error" className="max-w-[100ch] text-xs text-secondary">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
